import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import {
  getDefaultChatContext,
  getDocumentGroupQuery,
} from '~/features/ask-anything/ask-anything-button/utils';
import { usePermission } from '~/hooks/usePermission';
import { api } from '~/redux/api/createApi';
import { PersistRedux } from '~/redux/persist';
import { slices } from '~/redux/slices';
import { pathsToPersist } from '~/redux/slices/document-group';
import { useRouting } from '~/routing/useRouting';
import {
  MODAL_ADD_DOCUMENTS_TO_GROUP,
  MODAL_DELETE_DOCUMENTS_GROUP,
  MODAL_EDIT_DOCUMENTS_GROUP,
} from '~/types/modal.types';
import { ERROR } from '~/types/toast.types';

import AddDocuments from './AddDocuments/AddDocuments';
import { DeleteGroup } from './DeleteGroup/DeleteGroup';
import { EditGroup } from './EditGroup/EditGroup';
import GroupDocuments from './GroupDocuments/GroupDocuments';

function DocumentGroupPage() {
  const { params } = useRouting();
  const id = params.groupId!;
  const intl = useIntl();

  const dispatch = useDispatch();

  const { data, error, isFetching } = api.endpoints.getDocumentGroup.useQuery({
    id,
  });

  const { hasPermission: hasEditPermission, isLoading: isLoadingPermission } =
    usePermission({
      permission: { resourceId: 'document_groups', resourceType: 'edit' },
    });

  const title = data?.name;

  const showAddDocumentsToGroupModal = () => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_ADD_DOCUMENTS_TO_GROUP,
        context: {},
      }),
    );
  };

  const showEditGroupModal = useCallback(() => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_EDIT_DOCUMENTS_GROUP,
        context: {},
      }),
    );
  }, []);

  const showDeleteGroupModal = useCallback(() => {
    dispatch(
      slices.documentGroup.actions.setModal({
        modalType: MODAL_DELETE_DOCUMENTS_GROUP,
        context: {},
      }),
    );
  }, []);

  useEffect(() => {
    if (error) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DocumentGroupPage.loadingError',
          defaultMessage: 'An error occurred while loading the group data.',
          description: 'Error message when group data fails to load',
        }),
      );
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      dispatch(
        slices.chatbot.actions.setContext([
          getDefaultChatContext(intl),
          {
            label: intl.formatMessage({
              id: 'DocumentGroupPage.thisGroupLabel',
              defaultMessage: 'This Group',
              description: 'Label for current document group context',
            }),
            query: getDocumentGroupQuery(String(data.groupId)),
            title: data.name,
            icon: 'groups',
            primary: true,
            searchEntity: 'document',
            selectedIds: [],
            id: String(data.groupId),
            type: 'document_group',
          },
        ]),
      );
    }

    return () => {
      dispatch(
        slices.chatbot.actions.setContext([getDefaultChatContext(intl)]),
      );
    };
  }, [data]);

  const hasData = data && !error && !isFetching;

  if (hasData) {
    return (
      <>
        {pathsToPersist.map((path, i) => (
          <PersistRedux key={i} path={path!} />
        ))}
        <ContentContainer
          loadingContent={{
            isLoading: Boolean(isFetching || isLoadingPermission),
          }}
        >
          <Layout direction="column">
            <Layout direction="row" justify="space-between">
              <Text variant="title" as="h2" mt={1}>
                {title}
              </Text>
              <Layout>
                <Button
                  disabled={!hasEditPermission}
                  variant="primary"
                  text={intl.formatMessage({
                    id: 'DocumentGroupPage.addDocumentsButton',
                    defaultMessage: '+ Add Documents',
                    description: 'Button text for adding documents to group',
                  })}
                  onClick={showAddDocumentsToGroupModal}
                />
                <Layout ml={1}>
                  <ActionsMenu
                    disabled={!hasEditPermission}
                    name={intl.formatMessage({
                      id: 'DocumentGroupPage.groupActionsMenu',
                      defaultMessage: 'group actions',
                      description: 'Accessibility name for group actions menu',
                    })}
                    actions={[
                      {
                        label: intl.formatMessage({
                          id: 'DocumentGroupPage.editAction',
                          defaultMessage: 'Edit',
                          description: 'Edit action label in actions menu',
                        }),
                        value: 'edit',
                        onClick: showEditGroupModal,
                      },
                      {
                        label: intl.formatMessage({
                          id: 'DocumentGroupPage.deleteAction',
                          defaultMessage: 'Delete',
                          description: 'Delete action label in actions menu',
                        }),
                        value: 'delete',
                        onClick: showDeleteGroupModal,
                      },
                    ]}
                  />
                </Layout>
              </Layout>
            </Layout>
            <GroupDocuments
              groupId={id}
              numberOfDocs={data?.numberOfDocs}
              hasEditPermission={!!hasEditPermission}
            />
            <AddDocuments groupId={id} groupName={title} />
            <EditGroup
              groupId={Number(id)}
              name={data?.name}
              note={data?.note}
            />
            <DeleteGroup groupId={Number(id)} name={data?.name} />
          </Layout>
        </ContentContainer>
      </>
    );
  } else {
    return null;
  }
}

export default DocumentGroupPage;
